/**
 * NewSlideDialog.tsx — 轮播图（Banner slide）编辑弹窗
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs.jsx 1052–1117（NewSlideDialog）。
 * 书城首页 · 帧按语言拆行：本行 title_i18n 至多当前语言一个 key（dev-docs
 * `01-通用约定/85-运营配置维度模型.md` §5 + DB CHECK `banner_slides_title_i18n_valid`），
 * **不回退英语**。故只录当前语言一行、不开「其他语言」。标题选填（纯图片投放）：留空落 `{}`
 * （无标题唯一表示，§3.2.1 选填池例外；dev-docs#1534）。
 * 机械转换：JS→TS；window.__multiLang（非受控）→ 本地 useState + 受控 ~/ui/MultiLangFormRows；
 *   原型 DialogShell → ~/ui/Dialog；FormRow / Switch 收口共享 ~/ui；Select 就近内联。
 * 视觉层（JSX/className/inline-style）与原型逐字一致。
 *
 * 接真：
 *   - **新建（无 id）/ 编辑（带 id）slide** → createSlide（POST）/ updateSlide（PATCH /:slide_id），operator+ mfa：
 *     标题 i18n（选填；填则当前语言单 key 非空，留空 = `{}`，编辑时清空即清除已有标题）/ 跳转目标（target_type 闭集 book·offer·web，#228 决议 3 + target_value 分型校验）/ 排序 / 上架开关。
 *   - **图片上传**（ADM-OPS-S004）：选文件 → opsApi.uploadSlideImage（multipart → 写共享内容桶
 *     banners/{id}/slides/{assetId}.webp，C 端 presign 读同桶）→ 拿 image_r2_key 存 state（本地 objectURL 即时预览）；
 *     保存随 upsert 落库。图片不改时回传既有 image_r2_key（契约必填且须 banners/ 命名空间，C 端签名前强校前缀）。
 *   - 成功 → toast + invalidate（['banner-slides', bannerId] + ['banners']，mutation 内）；失败 → 错误 toast 不假成功。
 */

import type { CreateBannerSlideRequest, UpdateBannerSlideRequest } from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuthedImage } from '~/hooks/use-authed-image';
import { useBookLanguageRow } from '~/hooks/use-book-language-row';
import { opsApi } from '~/hooks/use-ops';
import { Icons } from '~/lib/icons';
import { BookLanguageRowPicker } from '~/ui/BookLanguageRowPicker';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { PaymentOfferPicker } from '~/ui/PaymentOfferPicker';
import { Switch } from '~/ui/Switch';
import { type OpsMutations, errorToast, successToast } from '../use-ops-mutations';
import type { Slide } from './banner-slide-view-model';

// ops 域弹窗内表单行：复用共享 ~/ui/FormRow，固定 dialogs 历史尺寸（labelWidth140 / gap14 / pad / labelPadTop6）
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** target_type 闭集 → 跳转目标分型校验提示（#228 决议 3：topup→offer，offer 寻址 app_payment_offers.id）*/
const TARGET_TABS: { id: 'book' | 'offer' | 'web'; label: string; placeholder: string }[] = [
  // book / offer 已改为选择器（见下方 renderTargetInput），placeholder 只对 web 的裸输入生效
  { id: 'book', label: '书籍', placeholder: '' },
  { id: 'offer', label: '运营活动', placeholder: '' },
  { id: 'web', label: '网页链接', placeholder: 'https://' },
];

/** web 打开方式（#304，仅 target_type=web 生效）：external=系统浏览器（默认兜底）/ in_app=App 内置浏览器 */
const OPEN_MODE_TABS: { id: 'external' | 'in_app'; label: string }[] = [
  { id: 'external', label: '系统浏览器' },
  { id: 'in_app', label: '内置浏览器' },
];

export function NewSlideDialog({
  bannerId,
  bannerName,
  bannerAppId,
  slide,
  lang = 'en',
  mutations,
  onClose,
}: {
  bannerId: string;
  bannerName: string;
  /** 位所属 App；`null` = 全局位。offer tab 据此禁用（写侧对全局位配 offer 无条件硬拒）*/
  bannerAppId: string | null;
  slide: Slide; // 仅编辑既有 slide（新建因无 R2 直传端点已禁用，见文件头）
  lang?: string;
  mutations: OpsMutations;
  onClose: () => void;
}) {
  const [target, setTarget] = useState<'book' | 'offer' | 'web'>(slide.targetType ?? 'book');
  const [targetValue, setTargetValue] = useState(slide.targetValue ?? '');
  // 已选书行详情（picker 展示封面 / 书名 / 语言用）。
  // **绑当前 targetValue、不是 slide.targetValue** —— 后者是打开弹窗时的初始值，换书后不会变，
  // 会让弹窗显示旧书封面/书名而保存的是新书；新建或原本非 book 的 slide 还会永远停在「加载书行…」。
  const selectedBookQ = useBookLanguageRow(targetValue, { enabled: target === 'book' });
  // web 打开方式（#304）：默认 external（= serving 对 NULL 的兜底）；仅 target=web 时录入 + 回传
  const [openMode, setOpenMode] = useState<'external' | 'in_app'>(slide.openMode ?? 'external');
  // 本行至多当前语言一个 key：既有帧回填本语言值，新建 / 无标题帧给空串。
  const [title, setTitle] = useState<Record<string, string>>(() => ({
    [lang]: slide.titleI18n?.[lang] ?? '',
  }));
  const [displayOrder, setDisplayOrder] = useState(String(slide.displayOrder ?? 1));
  // 新建态恒 false：服务端 §4.2 定 is_active=false，初值给 true 会让禁用后的显示与落库不一致
  const [active, setActive] = useState(slide.id ? (slide.isActive ?? true) : false);
  // 图片：上传后存新 image_r2_key（共享内容桶，banners/ 前缀供 C 端 presign）；本地 objectURL 即时预览
  const [imageR2Key, setImageR2Key] = useState(slide.imageR2Key ?? '');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // 编辑既有 slide 且未重新上传 → 经鉴权代理取已落库图片预览（image_url，A7 §2.2 口径）；新上传后本地预览优先、停取
  const { objUrl: persistedPreview, failed: persistedPreviewFailed } = useAuthedImage(
    imagePreview ? null : slide.imageUrl || null,
  );
  const previewUrl = imagePreview ?? persistedPreview;
  const uploadImg = useMutation({
    mutationFn: (file: File) => opsApi.uploadSlideImage(bannerId, file),
    onSuccess: (res, file) => {
      setImageR2Key(res.image_r2_key);
      // 重选时先 revoke 旧 objectURL，避免 blob URL 泄漏（adversarial）
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      successToast('图片已上传 · 保存后生效');
    },
    onError: (err) => errorToast(err, '图片上传失败'),
  });
  const langTitle = (title[lang] ?? '').trim();
  // offer picker 上报「这次保存必被写侧拒」（已上架 + 所选活动不在窗口内）时锁住 Save，
  // 别让运营去吃一个确定会发生的 422
  const [offerSelectionBlocked, setOfferSelectionBlocked] = useState(false);
  const formValid = !!targetValue.trim() && !(target === 'offer' && offerSelectionBlocked);
  const busy = mutations.createSlide.isPending || mutations.updateSlide.isPending;
  const isNew = !slide.id; // 无 id = 新建 → POST；有 id = 编辑 → PATCH /:slide_id

  const save = () => {
    if (!formValid || !imageR2Key) return;
    const handlers = {
      onSuccess: () => {
        successToast(isNew ? '已创建轮播图 · 默认下架，需在列表里上架' : '已保存轮播图');
        onClose();
      },
      onError: (err: unknown) => errorToast(err, isNew ? '创建轮播图失败' : '保存轮播图失败'),
    };
    // 两端共有的内容字段。`target_type` 与 `target_value` 一律同传：§4.3 允许各改各的，但只给
    // 一半会让分型校验落在错误的一侧（旧 type 判新 value / 新 type 配旧 value），写出 C 端静默
    // 丢弃的死链。`open_mode` 仅 target=web 传（#304：非 web 传入静默存 NULL）。
    // 标题留空恒传 `{}`：POST 等同省略；PATCH 下 `{}` = 清空已有标题（省略才是保持原值，A2 §4.3）
    const content = {
      title_i18n: langTitle ? { [lang]: langTitle } : {},
      image_r2_key: imageR2Key,
      target_type: target,
      target_value: targetValue.trim(),
      ...(target === 'web' ? { open_mode: openMode } : {}),
      display_order: Number(displayOrder) || 0,
    };

    if (!slide.id) {
      // POST（§4.2）：language 必填（本行服务的语言）；is_active 由服务端定 false，不传
      const body: CreateBannerSlideRequest = { language: lang, ...content };
      mutations.createSlide.mutate({ bannerId, body }, handlers);
      return;
    }
    // PATCH（§4.3）：**不传 language** —— 改它等于把该行搬到另一语言，契约明列不可改
    const body: UpdateBannerSlideRequest = { ...content, is_active: active };
    mutations.updateSlide.mutate({ bannerId, slideId: slide.id, body }, handlers);
  };

  const tab = TARGET_TABS.find((t) => t.id === target);

  return (
    <Dialog
      width={680}
      title={isNew ? '新建轮播图' : '编辑轮播图'}
      subtitle={`${bannerName} · 只维护 ${lang.toUpperCase()} 单 key`}
      icon="IconImg"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            其他语言请切到该语言芯片单独维护 · 该语言无配置时 C 端不展示，不回退英文
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!formValid || busy || uploadImg.isPending || !imageR2Key}
            title={
              !imageR2Key ? '请先上传轮播图' : !targetValue.trim() ? '跳转目标必填' : undefined
            }
            onClick={save}
          >
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="图片" hint="≤2MB · webp/png/jpeg · 各 App 按域名 serve（C 端 presign）">
          <label
            title="上传轮播图（root/admin/operator）"
            style={{
              width: '100%',
              maxWidth: 420,
              height: 130,
              borderRadius: 6,
              background: previewUrl
                ? `center/cover no-repeat url(${previewUrl})`
                : 'linear-gradient(135deg, var(--accent), oklch(0.4 0.1 30))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 6,
              color: 'rgba(255,255,255,0.9)',
              border: '1px dashed rgba(255,255,255,0.5)',
              cursor: uploadImg.isPending ? 'wait' : 'pointer',
              textShadow: previewUrl ? '0 1px 4px rgba(0,0,0,0.6)' : undefined,
            }}
          >
            <Icons.IconUpload size={18} />
            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
              {uploadImg.isPending
                ? '上传中…'
                : imagePreview
                  ? '点击重新选择'
                  : imageR2Key
                    ? persistedPreviewFailed
                      ? '已有图片 · 预览加载失败 · 点击换图'
                      : '已有图片 · 点击换图'
                    : '上传轮播图'}
            </span>
            <input
              type="file"
              accept="image/webp,image/png,image/jpeg"
              hidden
              disabled={uploadImg.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadImg.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
        </FormRow>
        <MultiLangFormRows
          label="标题"
          value={title}
          onChange={setTitle}
          requiredLangs={[]}
          pinnedLangs={[lang]}
          showOtherLangs={false}
          placeholderMap={{ zh: '标题 (中文)', en: 'Title (English)' }}
        />
        <FormRow label="跳转目标" required>
          <div className="seg" style={{ width: '100%' }}>
            {TARGET_TABS.map((t) => (
              <button
                key={t.id}
                className={target === t.id ? 'on' : ''}
                onClick={() => {
                  if (t.id === target) return;
                  setTarget(t.id);
                  // 切类型必须清空 target_value：三类的值域互不通用，而 book / offer **都是 UUID**，
                  // 未上架的 slide 服务端跳过 target 校验（banner-admin-service `validateTarget`：
                  // `if (!resultingActive) return`），残留值会被原样存下 —— 一个 novel UUID 存成
                  // offer_id，上架时才炸，且那时已看不出它本来是本书。
                  setTargetValue('');
                  // 切到非 web target 时重置 open_mode 为安全默认 external（codex #220 F2）：
                  // 避免旧 in_app 状态在「web→book→web」往返后作为 stale 值残留、绕过 external 兜底
                  if (t.id !== 'web') setOpenMode('external');
                }}
                style={{ flex: 1 }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            {/* book / offer 走选择器：这两类 target_value 是 UUID，手输选错了 serving 侧只会静默过滤、
                不报错也不下发（书行语言不符 / 活动不在窗口内），运营无从察觉。web 仍是自由 URL。 */}
            {target === 'book' && (
              <BookLanguageRowPicker
                value={targetValue}
                book={selectedBookQ.data}
                onChange={setTargetValue}
                // 位的语言决定候选：C 端按 novels.locale = slide 语言过滤，选错语言行即静默不下发
                lockedLocale={lang}
                // C 端 isBookServable 要求 effective status = LISTED，而写侧只校验存在性 + locale
                listedOnly
                // 区分「还在查」与「查不到」，否则冷缓存下每次打开都闪一下失效警告
                bookLookupFailed={
                  selectedBookQ.isError || (selectedBookQ.isSuccess && !selectedBookQ.data)
                }
              />
            )}
            {target === 'offer' && (
              <PaymentOfferPicker
                value={targetValue}
                appId={bannerAppId}
                // 上架开关是活的：切到上架时候选立刻收窄成严格判据，运营当场看到预配项消失，
                // 而不是等保存才吃 422。未上架时服务端跳过 target 校验，故放宽为可选未开始的活动。
                // strictWindow 表达的是「服务端会不会校验这个 target」，**不能跟 active 走**：
                // 服务端短路 `!resultingActive && mode==='patch'` 只对 PATCH 生效，
                // createBannerSlide 走 mode='replace' → 新建恒校验。新建态若跟着 active=false
                // 放宽 picker，运营能选到「即将开始」的 offer，POST 必被 OPS_BANNER_OFFER_NOT_FOUND 拒。
                strictWindow={!slide.id || active}
                onSelectionBlocked={setOfferSelectionBlocked}
                onChange={setTargetValue}
              />
            )}
            {target === 'web' && (
              <input
                className="input"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder={tab?.placeholder}
                style={{ width: '100%' }}
              />
            )}
          </div>
        </FormRow>
        {/* web 打开方式（#304）：仅 target_type=web 显示；默认系统浏览器（= serving 对 NULL 的兜底）*/}
        {target === 'web' && (
          <FormRow label="打开方式" hint="内置=App 内浏览器 · 系统=跳出浏览器（默认）">
            <div className="seg" style={{ width: '100%' }}>
              {OPEN_MODE_TABS.map((m) => (
                <button
                  key={m.id}
                  className={openMode === m.id ? 'on' : ''}
                  onClick={() => setOpenMode(m.id)}
                  style={{ flex: 1 }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </FormRow>
        )}
        <FormRow label="排序" hint="数字越小越靠前">
          <input
            className="input mono"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            style={{ width: 80, textAlign: 'right' }}
          />
        </FormRow>
        {/* 新建态本开关禁用：§4.2「is_active 由服务端定 false」——留着可切等于假成功
            （运营拨到上架、保存提示成功，落库却是下架、C 端不下发）*/}
        <FormRow
          label="上架状态"
          hint={slide.id ? undefined : '新建一律为下架（服务端定），创建完在列表里上架'}
        >
          <Switch
            on={slide.id ? active : false}
            size="sm"
            onChange={setActive}
            disabled={!slide.id}
          />
        </FormRow>
      </div>
    </Dialog>
  );
}
